import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GlassCard, PageHeader, ProgressBar, Pill, EmptyStateCard } from '../components/ui';
import { api } from '../lib/api';
import { addDaysToDateKey, localDateKey, localDateKeyFromIso } from '../lib/dateKey';
import { calorieTargetForDateKey } from '../lib/weeklyCalories';
import { formatWaterWithAlt, getWaterUnitPref } from '../lib/waterUnits';
import { btnPrimary, btnSecondary, btnToolbarActive, fieldControl } from '../lib/uiClasses';
import type { Meal, UserProfile, Workout } from '../types';

type WaterEntryRow = { id: string; ml: number; loggedAt: string };

type DaySlice = {
  totals: { calories: number; proteinG: number; carbsG: number; fatsG: number };
  waterMl: number;
  waterEntries?: WaterEntryRow[];
  meals: Meal[];
  sleepHours: number | null;
  sleepBedAt: string | null;
  sleepWakeAt: string | null;
  healthSteps?: number | null;
  healthActiveCalories?: number | null;
};

function formatClock(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function DayReviewPage() {
  const today = localDateKey();
  const [reviewKey, setReviewKey] = useState(() => addDaysToDateKey(today, -1));
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [day, setDay] = useState<DaySlice | null>(null);
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let c = false;
    (async () => {
      setLoading(true);
      try {
        const [p, d, w] = await Promise.all([
          api<UserProfile | null>('/api/profile'),
          api<DaySlice>(`/api/nutrition/day/${reviewKey}`),
          api<Workout[]>('/api/workouts'),
        ]);
        if (!c) {
          setProfile(p);
          setDay(d);
          setWorkouts(w);
        }
      } catch {
        if (!c) {
          setProfile(null);
          setDay(null);
          setWorkouts([]);
        }
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => {
      c = true;
    };
  }, [reviewKey]);

  const calorieTarget = calorieTargetForDateKey(profile, reviewKey);
  const proteinTarget = profile?.proteinTargetG ?? 150;
  const dayWorkouts = workouts.filter((w) => localDateKeyFromIso(w.startedAt) === reviewKey);
  const loggedBurn = dayWorkouts.reduce((s, w) => s + (w.estimatedCaloriesBurned ?? 0), 0);
  const burned = day?.healthActiveCalories ?? loggedBurn;
  const isToday = reviewKey === today;
  const label =
    reviewKey === today
      ? 'Today'
      : reviewKey === addDaysToDateKey(today, -1)
        ? 'Yesterday'
        : new Date(reviewKey + 'T12:00:00').toLocaleDateString(undefined, {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });

  const sleepLine =
    day?.sleepHours != null
      ? `${day.sleepHours} h`
      : day?.sleepBedAt && day?.sleepWakeAt
        ? `${formatClock(day.sleepBedAt)} → ${formatClock(day.sleepWakeAt)}`
        : null;

  const waterUnitPref = getWaterUnitPref();
  const isYesterday = reviewKey === addDaysToDateKey(today, -1);

  return (
    <>
      <PageHeader
        title="Day review"
        subtitle="One screen: meals, water, sleep, and training for any date."
      />

      <div className="mb-6 min-w-0 space-y-3">
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:gap-x-4">
          <label className="min-w-0 w-full shrink-0 sm:w-auto sm:max-w-[min(100%,20rem)]">
            <span className="mb-1 block text-xs font-medium text-zinc-400">Date</span>
            <input
              type="date"
              value={reviewKey}
              onChange={(e) => setReviewKey(e.target.value)}
              className={`${fieldControl} box-border mt-0`}
            />
          </label>
        </div>
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-stretch">
          <div className="flex min-w-0 flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setReviewKey(addDaysToDateKey(reviewKey, -1))}
              className={`${btnSecondary} px-3 py-2 text-xs sm:text-sm`}
            >
              ← Previous day
            </button>
            <button
              type="button"
              onClick={() => setReviewKey(addDaysToDateKey(reviewKey, 1))}
              className={`${btnSecondary} px-3 py-2 text-xs sm:text-sm`}
            >
              Next day →
            </button>
          </div>
          <div className="flex min-w-0 flex-wrap gap-2 sm:ml-auto">
            <button
              type="button"
              onClick={() => setReviewKey(today)}
              className={isToday ? `${btnToolbarActive} px-3 py-2 text-xs sm:text-sm` : `${btnSecondary} px-3 py-2 text-xs sm:text-sm`}
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => setReviewKey(addDaysToDateKey(today, -1))}
              className={isYesterday ? `${btnToolbarActive} px-3 py-2 text-xs sm:text-sm` : `${btnSecondary} px-3 py-2 text-xs sm:text-sm`}
            >
              Yesterday
            </button>
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-sm text-zinc-400">{label}</span>
        {isToday ? <Pill>Today</Pill> : null}
        {profile?.useWeeklyCalorieTargets ? (
          <span className="rounded-full border border-sky-500/25 bg-sky-500/10 px-2.5 py-0.5 text-[10px] font-medium text-sky-300">
            Weekly calorie schedule
          </span>
        ) : null}
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500">Loading…</p>
      ) : (
        <div className="grid min-w-0 gap-4 lg:grid-cols-2">
          <GlassCard>
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-semibold text-white">Nutrition</div>
              <Link
                to={`/nutrition?date=${encodeURIComponent(reviewKey)}`}
                className={`${btnSecondary} border-transparent bg-transparent px-2 py-1 text-xs shadow-none`}
              >
                Log meals →
              </Link>
            </div>
            <p className="mt-1 text-xs text-zinc-500">
              Goal this day: <span className="tabular-nums text-zinc-300">{calorieTarget} kcal</span>
            </p>
            <div className="mt-4 text-2xl font-semibold tabular-nums text-white">
              {day?.totals.calories ?? 0}{' '}
              <span className="text-base font-normal text-zinc-500">/ {calorieTarget}</span>
            </div>
            <div className="mt-2">
              <ProgressBar value={day?.totals.calories ?? 0} max={calorieTarget} />
            </div>
            <div className="mt-4 text-sm">
              <span className="text-zinc-500">Protein </span>
              <span className="font-medium tabular-nums text-zinc-200">
                {Math.round(day?.totals.proteinG ?? 0)}g / {proteinTarget}g
              </span>
            </div>
            <div className="mt-2">
              <ProgressBar value={day?.totals.proteinG ?? 0} max={proteinTarget} colorClass="bg-success" />
            </div>
            {(day?.meals ?? []).length === 0 ? (
              <div className="mt-4">
                <EmptyStateCard
                  title="No meals for this day"
                  description="Log food on Nutrition to see macros and calorie progress here."
                >
                  <Link to={`/nutrition?date=${encodeURIComponent(reviewKey)}`} className={btnPrimary}>
                    Log meal
                  </Link>
                </EmptyStateCard>
              </div>
            ) : (
              <ul className="mt-4 max-h-48 space-y-2 overflow-y-auto pr-1 text-sm nice-scroll">
                {day!.meals.map((m) => (
                  <li key={m.id} className="rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2">
                    <div className="font-medium text-zinc-200">{m.name}</div>
                    <div className="text-xs text-zinc-500">
                      {m.calories} kcal · P {m.proteinG}g · C {m.carbsG}g · F {m.fatsG}g ·{' '}
                      {new Date(m.loggedAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </GlassCard>

          <GlassCard delay={0.05}>
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-semibold text-white">Water & sleep</div>
              <div className="flex flex-wrap gap-2 text-xs font-medium">
                <Link
                  to={`/nutrition?date=${encodeURIComponent(reviewKey)}`}
                  className={`${btnSecondary} border-transparent bg-transparent px-2 py-1 shadow-none`}
                >
                  Water →
                </Link>
                <Link
                  to={`/sleep?date=${encodeURIComponent(reviewKey)}`}
                  className={`${btnSecondary} border-transparent bg-transparent px-2 py-1 shadow-none`}
                >
                  Sleep →
                </Link>
              </div>
            </div>
            <div className="mt-4 rounded-xl border border-cyan-500/15 bg-cyan-500/[0.06] px-3 py-3">
              <div className="text-[11px] font-medium uppercase tracking-wider text-cyan-200/80">Water</div>
              <div className="mt-1 text-base font-semibold tabular-nums text-white">
                {formatWaterWithAlt(day?.waterMl ?? 0, waterUnitPref)}
              </div>
              {(day?.waterEntries?.length ?? 0) > 0 ? (
                <div className="mt-2 max-h-24 space-y-1 overflow-y-auto text-[11px] text-zinc-500 nice-scroll">
                  {(day?.waterEntries ?? []).map((e) => (
                    <div key={e.id}>
                      {formatWaterWithAlt(e.ml, waterUnitPref)} ·{' '}
                      {new Date(e.loggedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="mt-3 rounded-xl border border-violet-500/15 bg-violet-500/[0.06] px-3 py-3">
              <div className="text-[11px] font-medium uppercase tracking-wider text-violet-200/80">Sleep</div>
              {sleepLine ? (
                <div className="mt-1 text-sm text-zinc-200">
                  <span className="tabular-nums">{sleepLine}</span>
                </div>
              ) : (
                <div className="mt-3">
                  <EmptyStateCard
                    title="No sleep logged for this day"
                    description="Bed and wake times help connect recovery to training and nutrition."
                  >
                    <Link to={`/sleep?date=${encodeURIComponent(reviewKey)}`} className={btnPrimary}>
                      Log sleep
                    </Link>
                  </EmptyStateCard>
                </div>
              )}
            </div>
          </GlassCard>
          <GlassCard delay={0.08} className="lg:col-span-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="text-sm font-semibold text-white">Workouts</div>
              <Link
                to="/workouts"
                className={`${btnSecondary} border-transparent bg-transparent px-2 py-1 text-xs shadow-none`}
              >
                Log workout →
              </Link>
            </div>
            {burned > 0 ? (
              <p className="mt-1 text-xs text-zinc-500">
                {day?.healthActiveCalories != null ? 'Active energy (Apple Health)' : 'Estimated burn this day'}:{' '}
                <span className="tabular-nums text-amber-200/90">~{burned.toLocaleString()} kcal</span>
                {day?.healthSteps != null && day.healthSteps > 0 ? (
                  <span className="text-zinc-600"> · {day.healthSteps.toLocaleString()} steps</span>
                ) : null}
              </p>
            ) : null}
            <ul className="mt-4 space-y-2 text-sm">
              {dayWorkouts.length === 0 ? (
                <li className="list-none">
                  <EmptyStateCard
                    title="No workout on this date"
                    description="Sessions are grouped by the start time you log. You can backdate a workout from the Workouts page."
                  >
                    <Link to="/workouts" className={btnPrimary}>
                      Log workout
                    </Link>
                  </EmptyStateCard>
                </li>
              ) : (
                dayWorkouts.map((w) => (
                  <li key={w.id} className="rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium text-zinc-200">{w.name}</span>
                      {w.estimatedCaloriesBurned != null && w.estimatedCaloriesBurned > 0 ? (
                        <span className="text-[11px] tabular-nums text-amber-200/90">~{w.estimatedCaloriesBurned} kcal</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-zinc-500">{new Date(w.startedAt).toLocaleString()}</div>
                    {w.durationMinutes != null && w.durationMinutes > 0 ? (
                      <div className="text-xs text-zinc-600">{w.durationMinutes} min</div>
                    ) : null}
                  </li>
                ))
              )}
            </ul>
          </GlassCard>
        </div>
      )}
    </>
  );
}
